import React, { useMemo, useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { AlertTriangle, TrendingDown, TrendingUp, Info } from 'lucide-react';

const fmt = (v) => (v || 0).toLocaleString('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });
const fmtCompact = (v) => {
  const abs = Math.abs(v || 0);
  if (abs >= 1000000) return `${(v / 1000000).toFixed(1)}M`;
  if (abs >= 1000) return `${Math.round(v / 1000)}k`;
  return Math.round(v || 0).toString();
};

// Stress color computation: ratio = outflows / max(inflows, 1)
// 0–0.5: green (calma); 0.5–0.8: yellow (attenzione); 0.8–1: orange (tensione); >1: red (deficit)
function stressLevel(inflow, outflow) {
  if (!outflow) return { level: 'none', label: 'Nessuna uscita', cls: 'bg-slate-50 text-slate-400 border-slate-100' };
  if (!inflow) return { level: 'red', label: 'Deficit (nessuna entrata)', cls: 'bg-red-100 text-red-900 border-red-300' };
  const ratio = outflow / inflow;
  if (ratio < 0.5) return { level: 'green', label: 'Cassa serena', cls: 'bg-emerald-100 text-emerald-900 border-emerald-300' };
  if (ratio < 0.8) return { level: 'yellow', label: 'Attenzione', cls: 'bg-yellow-100 text-yellow-900 border-yellow-300' };
  if (ratio <= 1.0) return { level: 'orange', label: 'Tensione', cls: 'bg-orange-200 text-orange-900 border-orange-400' };
  return { level: 'red', label: 'Deficit', cls: 'bg-red-200 text-red-900 border-red-400' };
}

function ringISOWeek(d) {
  // returns {year, week} according to ISO 8601
  const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dayNum = (date.getUTCDay() + 6) % 7; // Mon=0
  date.setUTCDate(date.getUTCDate() - dayNum + 3);
  const firstThursday = new Date(Date.UTC(date.getUTCFullYear(), 0, 4));
  const diff = (date - firstThursday) / 86400000;
  const week = 1 + Math.round((diff - 3 + ((firstThursday.getUTCDay() + 6) % 7)) / 7);
  return { year: date.getUTCFullYear(), week };
}

function weekRangeLabel(year, week) {
  // Build label "dd/MM – dd/MM"
  const simple = new Date(Date.UTC(year, 0, 1 + (week - 1) * 7));
  const dow = (simple.getUTCDay() + 6) % 7;
  const monday = new Date(simple);
  monday.setUTCDate(simple.getUTCDate() - dow);
  const sunday = new Date(monday);
  sunday.setUTCDate(monday.getUTCDate() + 6);
  const fmt = (d) => `${String(d.getUTCDate()).padStart(2, '0')}/${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
  return { label: `${fmt(monday)} – ${fmt(sunday)}`, monday: monday.toISOString().slice(0, 10), sunday: sunday.toISOString().slice(0, 10) };
}

const MESI = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];

export default function ConcentrationView({ movements }) {
  const [mode, setMode] = useState('weeks'); // weeks | months
  const [horizonMonths, setHorizonMonths] = useState(3);

  const today = useMemo(() => new Date(), []);

  const weeklyBuckets = useMemo(() => {
    // Build 4 weeks before today + horizonMonths*4 forward, ordered by monday date
    const map = new Map();
    const startWindow = new Date(today);
    startWindow.setDate(today.getDate() - 28);
    const endWindow = new Date(today);
    endWindow.setMonth(today.getMonth() + horizonMonths);

    for (const m of movements || []) {
      if (!m.date) continue;
      const d = new Date(m.date);
      if (d < startWindow || d > endWindow) continue;
      const { year, week } = ringISOWeek(d);
      const k = `${year}-W${String(week).padStart(2, '0')}`;
      if (!map.has(k)) {
        const { label, monday } = weekRangeLabel(year, week);
        map.set(k, { key: k, year, week, label, monday, in: 0, out: 0, items: [] });
      }
      const b = map.get(k);
      if (m.direction === 'in') b.in += m.amount || 0;
      else b.out += m.amount || 0;
      b.items.push(m);
    }
    return Array.from(map.values()).sort((a, b) => a.monday.localeCompare(b.monday));
  }, [movements, horizonMonths, today]);

  const monthlyBuckets = useMemo(() => {
    const map = new Map();
    const startWindow = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const endWindow = new Date(today.getFullYear(), today.getMonth() + horizonMonths + 1, 0);
    for (const m of movements || []) {
      if (!m.date) continue;
      const d = new Date(m.date);
      if (d < startWindow || d > endWindow) continue;
      const k = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      if (!map.has(k)) map.set(k, { key: k, year: d.getFullYear(), month: d.getMonth(), label: `${MESI[d.getMonth()]} ${d.getFullYear()}`, in: 0, out: 0, items: [] });
      const b = map.get(k);
      if (m.direction === 'in') b.in += m.amount || 0;
      else b.out += m.amount || 0;
      b.items.push(m);
    }
    return Array.from(map.values()).sort((a, b) => a.key.localeCompare(b.key));
  }, [movements, horizonMonths, today]);

  const buckets = mode === 'weeks' ? weeklyBuckets : monthlyBuckets;

  const stats = useMemo(() => {
    const reds = buckets.filter(b => stressLevel(b.in, b.out).level === 'red');
    const oranges = buckets.filter(b => stressLevel(b.in, b.out).level === 'orange');
    const yellows = buckets.filter(b => stressLevel(b.in, b.out).level === 'yellow');
    const totIn = buckets.reduce((s, b) => s + b.in, 0);
    const totOut = buckets.reduce((s, b) => s + b.out, 0);
    return { reds, oranges, yellows, totIn, totOut, net: totIn - totOut };
  }, [buckets]);

  const maxAbs = useMemo(() => {
    let m = 0;
    for (const b of buckets) m = Math.max(m, b.in, b.out);
    return m || 1;
  }, [buckets]);

  return (
    <div className="space-y-4" data-testid="treasury-concentration-view">
      {/* Controls */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex gap-2">
          <Button size="sm" variant={mode === 'weeks' ? 'default' : 'outline'} onClick={() => setMode('weeks')} data-testid="conc-mode-weeks">Settimane</Button>
          <Button size="sm" variant={mode === 'months' ? 'default' : 'outline'} onClick={() => setMode('months')} data-testid="conc-mode-months">Mesi</Button>
        </div>
        <div className="flex gap-1 items-center">
          <span className="text-xs text-slate-500 mr-1">Orizzonte:</span>
          {[1, 3, 6, 12].map(n => (
            <Button key={n} size="sm" variant={horizonMonths === n ? 'default' : 'outline'} onClick={() => setHorizonMonths(n)} data-testid={`conc-horizon-${n}`}>{n}m</Button>
          ))}
        </div>
      </div>

      {/* Alert banner */}
      {(stats.reds.length > 0 || stats.oranges.length > 0) && (
        <Card className="border-red-200 bg-red-50/40" data-testid="conc-alert">
          <CardContent className="p-3 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 shrink-0" />
            <div className="text-xs">
              <p className="font-semibold text-red-900">Periodi ad alto stress finanziario</p>
              <p className="text-red-700 mt-0.5">
                {stats.reds.length > 0 && <><strong>{stats.reds.length}</strong> in deficit · </>}
                {stats.oranges.length > 0 && <><strong>{stats.oranges.length}</strong> in tensione · </>}
                {stats.yellows.length > 0 && <><strong>{stats.yellows.length}</strong> in attenzione</>}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Aggregate KPIs */}
      <div className="grid grid-cols-3 gap-3">
        <Card><CardContent className="p-3">
          <p className="text-[10px] uppercase tracking-wider text-emerald-700 flex items-center gap-1"><TrendingUp className="w-3 h-3" />Entrate totali</p>
          <p className="text-xl font-bold text-emerald-700 font-mono">{fmt(stats.totIn)}</p>
        </CardContent></Card>
        <Card><CardContent className="p-3">
          <p className="text-[10px] uppercase tracking-wider text-red-700 flex items-center gap-1"><TrendingDown className="w-3 h-3" />Uscite totali</p>
          <p className="text-xl font-bold text-red-700 font-mono">{fmt(stats.totOut)}</p>
        </CardContent></Card>
        <Card><CardContent className="p-3">
          <p className="text-[10px] uppercase tracking-wider text-slate-600">Saldo netto</p>
          <p className={`text-xl font-bold font-mono ${stats.net >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>{fmt(stats.net)}</p>
        </CardContent></Card>
      </div>

      {/* Heatmap */}
      <Card><CardContent className="p-3">
        {buckets.length === 0 ? (
          <p className="text-center text-slate-400 py-8 text-sm">Nessun movimento nel periodo selezionato.</p>
        ) : (
          <div className="space-y-2">
            {buckets.map(b => {
              const s = stressLevel(b.in, b.out);
              const inW = (b.in / maxAbs) * 100;
              const outW = (b.out / maxAbs) * 100;
              const net = b.in - b.out;
              return (
                <div key={b.key} className={`border-2 rounded-lg p-2.5 ${s.cls}`} data-testid={`conc-bucket-${b.key}`}>
                  <div className="flex items-center justify-between mb-1.5 flex-wrap gap-1">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold">{b.label}</span>
                      <span className="text-[9px] uppercase tracking-wider opacity-70 px-1.5 py-0.5 rounded bg-white/60 border border-current">{s.label}</span>
                    </div>
                    <div className="flex items-center gap-3 text-[10px] font-mono">
                      <span>↑ {fmtCompact(b.in)}</span>
                      <span>↓ {fmtCompact(b.out)}</span>
                      <span className={`font-bold ${net >= 0 ? '' : ''}`}>Netto: {fmtCompact(net)} €</span>
                    </div>
                  </div>
                  {/* Inflow bar */}
                  <div className="relative h-2 rounded bg-white/40 overflow-hidden mb-1">
                    <div className="absolute inset-y-0 left-0 bg-emerald-600 rounded" style={{ width: `${inW}%` }}></div>
                  </div>
                  {/* Outflow bar */}
                  <div className="relative h-2 rounded bg-white/40 overflow-hidden">
                    <div className="absolute inset-y-0 left-0 bg-red-600 rounded" style={{ width: `${outW}%` }}></div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent></Card>

      {/* Legend */}
      <Card><CardContent className="p-3">
        <div className="flex items-start gap-2 mb-2">
          <Info className="w-4 h-4 text-slate-500 mt-0.5 shrink-0" />
          <p className="text-xs text-slate-600">
            Lo <strong>stress di cassa</strong> è calcolato come rapporto tra uscite ed entrate previste nel periodo.
            Maggiore è il rapporto, più alta è la pressione finanziaria.
          </p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[10px]">
          <div className="flex items-center gap-2 p-2 rounded border-2 bg-emerald-100 border-emerald-300 text-emerald-900"><span className="font-semibold">Serena</span><span>· uscite &lt; 50% entrate</span></div>
          <div className="flex items-center gap-2 p-2 rounded border-2 bg-yellow-100 border-yellow-300 text-yellow-900"><span className="font-semibold">Attenzione</span><span>· 50–80%</span></div>
          <div className="flex items-center gap-2 p-2 rounded border-2 bg-orange-200 border-orange-400 text-orange-900"><span className="font-semibold">Tensione</span><span>· 80–100%</span></div>
          <div className="flex items-center gap-2 p-2 rounded border-2 bg-red-200 border-red-400 text-red-900"><span className="font-semibold">Deficit</span><span>· uscite &gt; entrate</span></div>
        </div>
      </CardContent></Card>
    </div>
  );
}
